'use client';
import React, { useState } from 'react';
import GXGLayout from '@/components/GXGLayout';

const HeartIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
const GridIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>;
const ListIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>;
const ControllerIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M6 12h4M8 10v4M15 11h.01M17 13h.01"/></svg>;
const PlusIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const ChevronDownIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>;

const games = [
  { id: 1, title: 'Stellar Metroid', system: 'Super Nintendo (SNES)', genre: ['Action', 'Adventure'], year: 1994, condition: 'CIB • Mint', status: 'NEW', statusColor: 'badge-green', gradient: 'from-purple-900 via-indigo-900 to-blue-900', tab: 'all' },
  { id: 2, title: 'Chrono Quest', system: 'Super Nintendo (SNES)', genre: ['RPG', 'Adventure'], year: 1995, condition: 'Loose • Good', status: 'PLAYING', statusColor: 'badge-blue', gradient: 'from-orange-900 via-amber-900 to-yellow-900', tab: 'playable' },
  { id: 3, title: 'Legend of Hyrule', system: 'Super Nintendo (SNES)', genre: ['Action', 'Adventure'], year: 1991, condition: 'CIB • Near Mint', status: null, statusColor: '', gradient: 'from-yellow-900 via-amber-900 to-orange-900', tab: 'all' },
  { id: 4, title: 'Street Brawler II', system: 'Sega Genesis', genre: ['Fighting', '2 Player'], year: 1992, condition: 'CIB • Good', status: null, statusColor: '', gradient: 'from-red-900 via-rose-900 to-pink-900', tab: 'all' },
  { id: 5, title: 'Castle of the Night', system: 'PlayStation (PS1)', genre: ['Action', 'Adventure'], year: 1997, condition: 'CIB • Mint', status: 'RARE', statusColor: 'badge-gold', gradient: 'from-slate-900 via-purple-900 to-indigo-900', tab: 'rare' },
  { id: 6, title: 'Metal Ops', system: 'PlayStation (PS1)', genre: ['Stealth', 'Adventure'], year: 1998, condition: 'CIB • Near Mint', status: 'FOR SALE', statusColor: 'badge-pink', gradient: 'from-gray-900 via-slate-900 to-zinc-900', tab: 'for-sale' },
  { id: 7, title: 'Golden Agent 007', system: 'Nintendo 64 (N64)', genre: ['FPS', 'Adventure'], year: 1997, condition: 'Loose • Good', status: null, statusColor: '', gradient: 'from-amber-900 via-yellow-900 to-lime-900', tab: 'all' },
  { id: 8, title: 'Sonic Blast 2', system: 'Sega Genesis', genre: ['Platformer', 'Adventure'], year: 1992, condition: 'CIB • Near Mint', status: 'RARE', statusColor: 'badge-gold', gradient: 'from-blue-900 via-sky-900 to-cyan-900', tab: 'rare' },
];

const recentlyAdded = [
  { title: 'EarthBound', system: 'SNES', condition: 'CIB • Mint', time: '2h ago' },
  { title: 'Mega Man X', system: 'SNES', condition: 'Loose • Good', time: '5h ago' },
  { title: 'Panzer Dragoon', system: 'Saturn', condition: 'CIB • Good', time: '1d ago' },
  { title: 'Resident Evil 2', system: 'PS1', condition: 'CIB • Near Mint', time: '2d ago' },
];

const tabs = ['All Games', 'Playable', 'For Sale', 'Rare', 'Featured'];

export default function LibraryPage() {
  const [activeTab, setActiveTab] = useState('All Games');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [search, setSearch] = useState('');
  const [likedGames, setLikedGames] = useState<number[]>([]);

  const toggleLike = (id: number) => {
    setLikedGames(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const filteredGames = games.filter(g => {
    const matchSearch = g.title.toLowerCase().includes(search.toLowerCase());
    const matchTab = activeTab === 'All Games' || activeTab === 'Featured' ||
      (activeTab === 'Playable' && g.tab === 'playable') ||
      (activeTab === 'For Sale' && g.tab === 'for-sale') ||
      (activeTab === 'Rare' && g.tab === 'rare');
    return matchSearch && matchTab;
  });

  return (
    <GXGLayout>
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display font-black text-3xl md:text-4xl text-white mb-2">Game Library</h1>
            <p className="text-white/50 text-sm max-w-lg">Browse your GXG collection. Rediscover classics, track your progress, and find your next adventure.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {[
              { icon: <ControllerIcon />, label: 'TOTAL GAMES', value: '328', sub: 'Across 12 Systems', color: 'text-gxg-purple2' },
              { icon: null, label: 'PLAYABLE NOW', value: '127', sub: 'Ready to Play', color: 'text-gxg-green' },
              { icon: null, label: 'FOR SALE', value: '23', sub: 'List your games', color: 'text-gxg-pink2' },
              { icon: null, label: 'RARE TITLES', value: '18', sub: "Collector's Gems", color: 'text-gxg-gold' },
            ].map((stat, i) => (
              <div key={i} className="glass-card px-4 py-3 text-center min-w-[100px]">
                <div className="text-[9px] font-mono text-white/35 uppercase tracking-wider mb-1">{stat.label}</div>
                <div className={`font-display font-bold text-xl ${stat.color}`}>{stat.value}</div>
                <div className="text-[10px] text-white/40">{stat.sub}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="xl:col-span-3">
            {/* Tabs */}
            <div className="flex items-center gap-1 mb-4 border-b border-white/8 overflow-x-auto pb-0">
              {tabs.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors border-b-2 -mb-px ${
                    activeTab === tab
                      ? 'text-gxg-purple2 border-gxg-purple2' :'text-white/50 border-transparent hover:text-white/80'
                  }`}
                >
                  {tab}
                </button>
              ))}
              <div className="flex-1" />
              <div className="flex items-center gap-2 pb-2">
                <select className="gxg-select text-xs py-1.5 px-3 w-auto">
                  <option>Recently Added</option>
                  <option>A-Z</option>
                  <option>Year</option>
                  <option>Condition</option>
                </select>
                <button onClick={() => setViewMode('grid')} className={`p-2 rounded ${viewMode === 'grid' ? 'bg-purple-500/20 text-gxg-purple2' : 'text-white/40 hover:text-white/70'}`}><GridIcon /></button>
                <button onClick={() => setViewMode('list')} className={`p-2 rounded ${viewMode === 'list' ? 'bg-purple-500/20 text-gxg-purple2' : 'text-white/40 hover:text-white/70'}`}><ListIcon /></button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <div className="gxg-search max-w-xs">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white/30"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search games..." />
              </div>
              {['System', 'Genre', 'Status', 'Year'].map(f => (
                <div key={f} className="flex items-center gap-1">
                  <span className="text-xs text-white/40 font-semibold">{f}</span>
                  <select className="gxg-select text-xs py-1.5 px-2 w-auto">
                    <option>All {f}s</option>
                  </select>
                </div>
              ))}
              <button className="text-xs text-gxg-purple2 hover:text-gxg-pink transition-colors">Clear Filters</button>
            </div>

            {/* Game Grid */}
            <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1'}`}>
              {filteredGames.map(game => (
                <div key={game.id} className="game-card group">
                  <div className={`relative h-44 bg-gradient-to-br ${game.gradient} flex items-end`}>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    {game.status && (
                      <span className={`absolute top-2 left-2 ${game.statusColor} text-[10px]`}>{game.status}</span>
                    )}
                    <button
                      onClick={() => toggleLike(game.id)}
                      className={`absolute top-2 right-2 w-7 h-7 rounded-full bg-black/40 flex items-center justify-center transition-colors ${likedGames.includes(game.id) ? 'text-red-400' : 'text-white/50 hover:text-red-400'}`}
                    >
                      <HeartIcon />
                    </button>
                    <div className="relative z-10 p-3 w-full">
                      <div className="font-display font-bold text-sm text-white leading-tight">{game.title}</div>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-1 text-[11px] text-white/50 mb-2">
                      <ControllerIcon />
                      <span>{game.system}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {game.genre.map(g => (
                        <span key={g} className="text-[10px] bg-white/5 border border-white/8 rounded px-1.5 py-0.5 text-white/50">{g}</span>
                      ))}
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-white/35">{game.year}</span>
                      <span className="text-gxg-green font-semibold">{game.condition}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More */}
            <div className="flex justify-center mt-6">
              <button className="btn-gxg-outline px-8 py-3 rounded-lg flex items-center gap-2 text-sm">
                Load More Games <ChevronDownIcon />
              </button>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-5">
            {/* Featured Collection */}
            <div className="glass-card p-4 neon-border-purple">
              <div className="h-32 rounded-lg bg-gradient-to-br from-yellow-900 via-orange-900 to-red-900 flex items-center justify-center mb-3 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="relative z-10 text-center">
                  <div className="font-display font-black text-lg text-gxg-gold">16-BIT LEGENDS</div>
                  <div className="text-xs text-white/60">THE GOLDEN ERA</div>
                </div>
              </div>
              <h3 className="font-display font-bold text-sm text-white mb-1">16-Bit Legends</h3>
              <p className="text-xs text-white/50 mb-3">The pinnacle of retro gaming. Iconic titles that defined a generation.</p>
              <button className="btn-gxg-primary text-xs w-full py-2 rounded-lg">Explore Collection</button>
            </div>

            {/* Recently Added */}
            <div className="glass-card p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-sm text-white">Recently Added</h3>
                <button className="text-gxg-purple2 text-xs">View All</button>
              </div>
              <div className="space-y-2">
                {recentlyAdded.map((game, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/3 transition-colors">
                    <div className="w-8 h-8 rounded bg-gradient-to-br from-purple-800 to-blue-800 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-white/90 truncate">{game.title}</div>
                      <div className="text-[10px] text-white/40">{game.system}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[10px] text-gxg-green">{game.condition}</div>
                      <div className="text-[10px] text-white/30">{game.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Can't find a game */}
            <div className="glass-card p-4 neon-border-blue">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <ControllerIcon />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white/90 mb-1">Can&apos;t find a game?</div>
                  <div className="text-xs text-white/50 mb-3">Request it for the collection or suggest a trade.</div>
                  <button className="btn-gxg-outline text-xs py-2 px-3 rounded-lg flex items-center gap-1">
                    <PlusIcon /> Add Game to Collection
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </GXGLayout>
  );
}
